import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Badge, Button, Drawer, Empty, Spin, notification } from "antd";
import {
  BellOutlined,
  PhoneOutlined,
  CloseOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { io } from "socket.io-client";
import moment from "moment";
import { ContextAPI } from "../../../ContextAPI";
import { platformApi } from "../../../api";
import { ENDPOINT } from "../../../constants";
import {
  ensureTelecmiThenCloudCall,
  getNotificationDialPhone,
} from "../../../utils/notificationCloudCall";

const TEAL = "#0D9488";
const TEAL_SOFT = "#CCFBF1";
const ROSE_SOFT = "#FCE7F3";
const ROSE = "#E11D48";
const NAVY = "#0F172A";
const MUTED = "#64748B";

const isMissedCall = (item) =>
  /missed\s*call/i.test(item?.title || "") ||
  /missed\s*call/i.test(item?.body || "");

const getCallerLabel = (item) => {
  const phone = getNotificationDialPhone(item);
  const body = item?.body || "";
  const m = body.match(/from\s+(.+?)(?:\s*\(|$)/i);
  const name = m?.[1]?.trim();
  if (name) return name;
  return phone ? `+91 ${phone}` : "Unknown";
};

const NotificationCard = ({ item, callingId, onCall, onMarkRead }) => {
  const phone = getNotificationDialPhone(item);
  const canCall = Boolean(phone);
  const missed = isMissedCall(item);
  const caller = getCallerLabel(item);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #EEF2F7",
        borderRadius: 16,
        marginBottom: 14,
        padding: 16,
        boxShadow: "0 4px 14px rgba(15,23,42,0.06)",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: missed ? ROSE_SOFT : TEAL_SOFT,
            color: missed ? ROSE : TEAL,
            fontSize: 20,
          }}
        >
          {missed ? <PhoneOutlined /> : <BellOutlined />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: 16,
                color: NAVY,
                letterSpacing: -0.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.title}
            </div>
            <span
              style={{
                flexShrink: 0,
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                background: item.isRead ? "#F1F5F9" : TEAL,
                color: item.isRead ? MUTED : "#fff",
              }}
            >
              {item.isRead ? "Read" : "New"}
            </span>
          </div>

          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: NAVY,
              marginBottom: 4,
            }}
          >
            {caller}
          </div>

          {item.body ? (
            <div
              style={{
                fontSize: 13,
                color: MUTED,
                lineHeight: 1.45,
              }}
            >
              {item.body}
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            minWidth: 0,
            flex: 1,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              color: MUTED,
              fontWeight: 500,
            }}
          >
            <CalendarOutlined style={{ color: TEAL, fontSize: 12 }} />
            {item.createdAt
              ? moment(item.createdAt).format("DD-MM-YYYY HH:mm")
              : ""}
          </span>
          <span
            style={{
              width: 1,
              height: 12,
              background: "#CBD5E1",
              display: "inline-block",
            }}
          />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              color: MUTED,
              fontWeight: 500,
            }}
          >
            <ClockCircleOutlined style={{ fontSize: 12 }} />
            {item.createdAt ? moment(item.createdAt).fromNow() : ""}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!item.isRead ? (
            <Button
              size="small"
              icon={<CheckOutlined />}
              onClick={() => onMarkRead(item.id)}
              title="Mark as read"
              style={{
                borderRadius: 8,
                height: 34,
                paddingInline: 12,
                color: TEAL,
                borderColor: "#99F6E4",
                background: "#F0FDFA",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Mark read
            </Button>
          ) : null}
          {canCall ? (
            <Button
              type="primary"
              size="small"
              icon={<PhoneOutlined />}
              loading={callingId === item.id}
              onClick={() => onCall(item)}
              style={{
                background: TEAL,
                borderColor: TEAL,
                borderRadius: 10,
                fontWeight: 700,
                height: 34,
                paddingInline: 14,
              }}
            >
              Call back
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const NotificationsBell = () => {
  const history = useHistory();
  const { loginCredintials } = useContext(ContextAPI);
  const [hasAccess, setHasAccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [callingId, setCallingId] = useState(null);

  const unreadInList = useMemo(
    () => items.filter((n) => !n.isRead).length,
    [items]
  );

  useEffect(() => {
    const ok =
      loginCredintials?.roleAccess &&
      loginCredintials.roleAccess.some(
        (each) =>
          String(each.subModule || "").toLowerCase() === "notifications" &&
          each.access?.read
      );
    setHasAccess(Boolean(ok));
  }, [loginCredintials]);

  const fetchUnread = useCallback(() => {
    if (!hasAccess) return;
    platformApi
      .get("/api/notifications/unreadCount")
      .then((res) => {
        if (res.data?.code === 200) {
          setUnreadCount(res.data.data?.unreadCount || 0);
        }
      })
      .catch(() => {});
  }, [hasAccess]);

  const fetchList = useCallback(() => {
    if (!hasAccess) return;
    setLoading(true);
    platformApi
      .get("/api/notifications", { params: { page: 1, size: 50 } })
      .then((res) => {
        if (res.data?.code === 200) {
          const all = res.data.data?.notifications || [];
          // Sidebar: only unread / unacted notifications
          setItems(all.filter((n) => !n.isRead));
          setUnreadCount(res.data.data?.unreadCount || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hasAccess]);

  useEffect(() => {
    if (!hasAccess) return;
    fetchUnread();
    const openDrawer = () => setOpen(true);
    window.addEventListener("open-notifications-drawer", openDrawer);

    const token = localStorage.getItem("token");
    if (!token || !ENDPOINT) {
      return () =>
        window.removeEventListener("open-notifications-drawer", openDrawer);
    }
    const socket = io(ENDPOINT, { auth: { token } });
    socket.on("userNotification", (payload) => {
      const n = payload?.data;
      if (!n) return;
      setUnreadCount((c) => c + 1);
      setItems((prev) => [n, ...prev]);
      notification.info({
        message: n.title || "New notification",
        description: n.body,
        duration: 4,
        placement: "topRight",
      });
    });
    return () => {
      socket.disconnect();
      window.removeEventListener("open-notifications-drawer", openDrawer);
    };
  }, [hasAccess, fetchUnread]);

  useEffect(() => {
    if (open && hasAccess) fetchList();
  }, [open, hasAccess, fetchList]);

  const markRead = (id) => {
    platformApi
      .post(`/api/notifications/${id}/read`)
      .then((res) => {
        if (res.data?.code === 200) {
          setItems((prev) => prev.filter((n) => n.id !== id));
          setUnreadCount((c) => Math.max(0, c - 1));
        }
      })
      .catch(() => {});
  };

  const handleCall = async (item) => {
    if (callingId) return;
    setCallingId(item.id);
    setOpen(false);
    try {
      const result = await ensureTelecmiThenCloudCall({
        notification: item,
        employeeName: loginCredintials?.employeeName,
        markRead: true,
      });
      if (result.ok) {
        // Acted on (Call back) → mark read + remove from sidebar
        setItems((prev) => prev.filter((n) => n.id !== item.id));
        if (!item.isRead) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
      }
    } finally {
      setCallingId(null);
    }
  };

  if (!hasAccess) return null;

  return (
    <>
      <span
        onClick={() => setOpen(true)}
        style={{
          marginRight: 16,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
        }}
        title="Notifications"
      >
        <Badge count={unreadCount} overflowCount={99} size="small">
          <BellOutlined style={{ fontSize: 20, color: "rgba(0,0,0,0.65)" }} />
        </Badge>
      </span>

      <Drawer
        title={null}
        placement="right"
        width={440}
        visible={open}
        onClose={() => setOpen(false)}
        closable={false}
        bodyStyle={{
          padding: 0,
          background: "#F8FAFC",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        style={{ transition: "transform 0.4s ease" }}
        destroyOnClose={false}
      >
        <div
          style={{
            padding: "18px 20px 14px",
            background: "#fff",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BellOutlined style={{ fontSize: 22, color: TEAL }} />
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: NAVY,
                  letterSpacing: -0.3,
                  lineHeight: 1.2,
                }}
              >
                Notifications
              </div>
              <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                {unreadInList > 0
                  ? `${unreadInList} unread`
                  : "You are all caught up"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setOpen(false);
                history.push("/autoadmin/notifications");
              }}
              style={{ color: TEAL, fontWeight: 700, paddingInline: 8 }}
            >
              View all <ArrowRightOutlined style={{ fontSize: 11 }} />
            </Button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                border: "none",
                background: "#F1F5F9",
                width: 32,
                height: 32,
                borderRadius: 16,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: MUTED,
              }}
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin />
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 16px" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 32,
                  background: TEAL_SOFT,
                  color: TEAL,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  marginBottom: 12,
                }}
              >
                <BellOutlined />
              </div>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    <div style={{ fontWeight: 700, color: NAVY }}>
                      No notifications
                    </div>
                    <div style={{ color: MUTED, fontSize: 12 }}>
                      Missed calls and alerts will show up here
                    </div>
                  </span>
                }
              />
            </div>
          ) : (
            items.map((item) => (
              <NotificationCard
                key={item.id}
                item={item}
                callingId={callingId}
                onCall={handleCall}
                onMarkRead={markRead}
              />
            ))
          )}
        </div>
      </Drawer>
    </>
  );
};

export default NotificationsBell;
